import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LinkIcon from '@mui/icons-material/Link';
import MailIcon from '@mui/icons-material/MailOutline';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { CircularProgress, Divider, IconButton, Popover, Stack, Switch } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ReactComponent as BellIcon } from "../../../assets/icons/gray-bell.svg";
import { ReactComponent as BuildingIcon } from "../../../assets/icons/gray-building.svg";
import { ReactComponent as ChromeIcon } from "../../../assets/icons/gray-chrome.svg";
import { ReactComponent as DatabaseIcon } from "../../../assets/icons/gray-db.svg";
import awsExports from "../../../aws-exports";
import { AuthContext } from '../../../contexts/AuthContext';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { getUserEmail } from "../../../helpers/authUser";
import { getUserFunc } from "../../../lib/helper";
import { ChromeExtensionLink, scrollbarStyle } from '../../../shared/dashboard';
import { User } from "../../../types/common";
import ForwardDealsModern from '../../atoms/ForwardDealsModern';
import ManageFieldsLoaderModal from '../home-manage-fields/ManageFieldsLoaderModal';
import GlobalPreferencesModal from '../investors/GlobalPreferencesModal';
import CompaniesCountdown from './CompaniesCountdown';
import ExcludeCompanyModal from './ExcludeCompanyModal';

const { app_domain } = awsExports;

const useStyles = makeStyles((theme) => ({
    modal: {
        width: 620,
        zIndex: 1300,
        '& > .MuiPaper-root': {
            width: '100%',
            height: 'fit-content',
            padding: '16px 12px',
            marginTop: 8,
            borderRadius: 16,
            overflow: 'hidden',
        }
    },
    title: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        fontSize: '1.5rem',
        color: '#000',
    },
    subtitle: {
        fontFamily: 'Inter',
        fontWeight: 500,
        fontSize: '0.95rem',
        color: '#666666',
    },
    field: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 600,
        color: '#212734',
    },
    description: {
        fontFamily: 'Lato',
        fontWeight: 500,
        fontSize: '0.95rem',
        color: '#747887',
    },
    setting: {
        fontFamily: 'Lato',
        fontWeight: 600,
        fontSize: '0.9rem',
        color: '#6c7181',
    },
    settingsList: {
        width: '100%',
        height: '100%',
        maxHeight: 700,
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    chip: {
        marginLeft: 12,
        padding: '4px 8px',
        fontFamily: 'Inter',
        fontWeight: 600,
        fontSize: '0.85rem',
        color: '#4b71df',
        background: '#dae9ff',
        borderRadius: 4,
    },
    action: {
        width: '100%',
        fontWeight: 'bold',
        color: '#7bd4d4',
        "&:hover": {
            background: 'rgba(4, 130, 144, 0.04)'
        },
        cursor: 'pointer',
    },
    mainIcon: {
        width: 32,
        height: 32,
        fill: '#666666',
    },
    menuLabel: {
        fontFamily: 'Inter',
        fontWeight: 400,
        fontSize: '0.9rem',
        color: theme.colors.neutral['600'],
        textTransform: 'none',
        "&:hover": {
            background: 'transparent',
            color: theme.colors.neutral['900'],
            fontWeight: 600,
        },
    },
    menuIcon: {
        width: 24,
        height: 24,
    },
    navigateIcon: {
        marginRight: 16,
        '& > svg': {
            width: 16,
            height: 16,
            fill: '#aeaeae',
        },
        "&:hover": {
            '& > svg': {
                fill: theme.colors.neutral['900'],
            },
        },
    },
    switchButton: {
        width: 80,
        height: 36,
        padding: 0,
        transform: 'scale(0.7)',
        '& .MuiSwitch-switchBase': {
            padding: '6px',
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(40px)',
                '& + .MuiSwitch-track': {
                    border: '2px solid #15b8a5',
                    backgroundColor: '#15b8a5',
                    opacity: 1,
                },
                '& .MuiSwitch-thumb': {
                    color: '#fff',
                },
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 24,
            height: 24,
            color: '#15b8a5',
        },
        '& .MuiSwitch-track': {
            borderRadius: '20px',
            border: '2px solid #15b8a5',
            backgroundColor: '#fff',
            opacity: 1,
        },
    },
}));

const GeneralSettings: React.FC<{ flags?: any }> = ({ flags }) => {
    const history = useHistory();
    const classes = useStyles();
    const { isNotissiaAdmin, userGroup, user } = useContext(AuthContext);
    const { externalSync, autoIngestFromEmail, autoSendDigestEmail, autoSendIngestionNotifications,
        setExternalSync, setAutoIngestFromEmail, setAutoSendDigestEmail, setAutoSendIngestionNotification } = useContext(GroupSettingsContext);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [copying, setCopying] = useState<boolean>(false);
    const [excludeCompanyModalOpen, setExcludedCompanyModalOpen] = useState<boolean>(false);
    const [preferencesModalOpen, setPreferencesModalOpen] = useState<boolean>(false);
    const [manageFieldsModalOpen, setManageFieldsModalOpen] = useState<boolean>(false);

    const handleAutoIngestFromEmail = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        setAutoIngestFromEmail(event.target.checked);
    }, [setAutoIngestFromEmail]);

    const handleExternalSync = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        setExternalSync(event.target.checked);
    }, [setExternalSync]);

    const handleAutoSendDigestEmail = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        setAutoSendDigestEmail(event.target.checked);
    }, [setAutoSendDigestEmail]);

    const handleIngestNotification = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        setAutoSendIngestionNotification(event.target.checked);
    }, [setAutoSendIngestionNotification]);

    // const handlePreferences = useCallback(() => {
    //     setPreferencesModalOpen(true);
    //     setAnchorEl(null);
    // }, []);

    const handleExclude = useCallback(() => {
        setExcludedCompanyModalOpen(true);
        setAnchorEl(null);
    }, []);

    const handleCopy = useCallback(async () => {
        setCopying(true);

        const referralLink: string = await new Promise((resolve) => setTimeout(() => {
            const userEmail = getUserEmail(user);

            if (!!userEmail) {
                getUserFunc({email: userEmail}).then((userData?: User) => {
                    if (userData?.referralCode)
                        resolve(`https://${app_domain}/referral/${userData?.referralCode}`);
                    else
                        resolve('');
                });
            } else {
                resolve('');
            }
        }, 500));

        navigator.clipboard.write([new ClipboardItem({
            'text/plain': new Blob([referralLink], { type: 'text/plain' }),
            'text/html': new Blob([referralLink], { type: 'text/html' }),
        })]);
        setAnchorEl(null);
        setCopying(false);
    }, [user]);

    return (<>
        <Button variant="text"
            onClick={event => setAnchorEl(event.currentTarget)}
            endIcon={<MenuIcon className={classes.mainIcon} />}
            sx={{ "&:hover": { background: 'transparent' } }}
            disableRipple />
        <Popover open={Boolean(anchorEl)}
            className={classes.modal}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center', }}
            transformOrigin={{ vertical: 'top', horizontal: 'center', }}
            onClose={() => setAnchorEl(null)}>
            <Stack direction="column" spacing={2} alignItems="flex-start" justifyContent="center" width="100%">
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                        <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center" pl={1}>
                            <Typography className={classes.title}>{'Settings'}</Typography>
                            <Typography className={classes.subtitle}>
                                {'Manage your workspace preferences'}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" pr={1}>
                        <CompaniesCountdown />
                    </Stack>
                </Stack>
                <Divider sx={{ width: '100%' }} />
                <Stack direction="column" className={classes.settingsList} spacing={3} alignItems="flex-start">
                    <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" pl="4px">
                            <Typography className={classes.setting}>
                                {'AUTOMATION'}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                <DatabaseIcon className={classes.menuIcon} />
                                <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                    <Typography className={classes.field}>
                                        {'Auto-add companies from emails'}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {'Automatically detect and add companies from your emails'}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <Switch className={classes.switchButton} checked={autoIngestFromEmail} onChange={handleAutoIngestFromEmail} disableRipple />
                            </Stack>
                        </Stack>
                        {flags.zapierPlan && (
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                    <BuildingIcon className={classes.menuIcon} />
                                    <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                        <Typography className={classes.field}>
                                            {'Auto-sync to external tools'}
                                        </Typography>
                                        <Typography className={classes.description}>
                                            {'Keep your CRM and other tools up to date'}
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                    <Switch className={classes.switchButton} checked={externalSync} onChange={handleExternalSync} disableRipple />
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                    <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" pl="4px">
                            <Typography className={classes.setting}>
                                {'EMAIL NOTIFICATIONS'}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start"justifyContent="flex-start">
                                <BellIcon className={classes.menuIcon} />
                                <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                    <Typography className={classes.field}>
                                        {'Weekly deal flow digest'}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {'Receive a summary of new companies every week'}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <Switch className={classes.switchButton} checked={autoSendDigestEmail} onChange={handleAutoSendDigestEmail} disableRipple />
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start"justifyContent="flex-start">
                                <MailIcon className={classes.menuIcon} />
                                <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                    <Typography className={classes.field}>
                                        {'No company & existing company alert'}
                                        <Typography className={classes.chip} display="inline">
                                            {'New'}
                                        </Typography>
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {'Get notified when no companies or existing companies are found'}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <Switch className={classes.switchButton} checked={autoSendIngestionNotifications} onChange={handleIngestNotification} disableRipple />
                            </Stack>
                        </Stack>
                    </Stack>
                    <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" pl="4px">
                            <Typography className={classes.setting}>
                                {'WORKSPACE'}
                            </Typography>
                        </Stack>
                        {/* <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                <SettingsIcon className={classes.menuIcon} />
                                <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                    <Typography className={classes.field}>
                                        {'Deal Preferences'}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {'Customize your deal tracking and filters'}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <IconButton className={classes.navigateIcon}
                                    onClick={handlePreferences}>
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            </Stack>
                        </Stack> */}
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                <BuildingIcon className={classes.menuIcon} />
                                <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                    <Typography className={classes.field}>
                                        {'Excluded companies'}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {'Manage companies you want to ignore'}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <IconButton className={classes.navigateIcon}
                                    onClick={handleExclude}>
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                <DatabaseIcon className={classes.menuIcon} />
                                <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                    <Typography className={classes.field}>
                                        {'Custom fields'}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {'Add and manage custom data fields'}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <IconButton className={classes.navigateIcon}
                                    onClick={() => setManageFieldsModalOpen(true)}>
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Stack>
                    <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" pl="4px">
                            <Typography className={classes.setting}>
                                {'ACCOUNT'}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                <PersonOutlineIcon className={classes.menuIcon} />
                                <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                    <Typography className={classes.field}>
                                        {'Plan & billing'}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {'Manage susbcrption and payment details'}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <IconButton className={classes.navigateIcon}
                                    onClick={() => history.push('/subscriptions')}>
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                        {(isNotissiaAdmin || flags.userAdmin) && (
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                    <PersonOutlineIcon className={classes.menuIcon} />
                                    <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                        <Typography className={classes.field}>
                                            {'Team members'}
                                        </Typography>
                                        <Typography className={classes.description}>
                                            {'Invite and manage workspace users'}
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                    <IconButton className={classes.navigateIcon}
                                        onClick={() => history.push(isNotissiaAdmin ? '/groups' : `/groups/${userGroup}`)}>
                                        <ArrowForwardIosIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                    <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" pl="4px">
                            <Typography className={classes.setting}>
                                {'INTEGRATION & TOOLS'}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                {copying ? ( <CircularProgress size="24px" /> )
                                    : ( <LinkIcon className={classes.menuIcon} /> )}
                                <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                    <Typography className={classes.field}>
                                        {'Referral link'}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {'Share and earn reward'}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <IconButton className={classes.navigateIcon}
                                    onClick={handleCopy}>
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={1}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="flex-start">
                                <ChromeIcon className={classes.menuIcon} />
                                <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                    <Typography className={classes.field}>
                                        {'Chrome Extension'}
                                    </Typography>
                                    <Typography className={classes.description}>
                                        {'Install browser extension for quick access'}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                <IconButton className={classes.navigateIcon}
                                    onClick={() => window.open(ChromeExtensionLink, '_blank', 'noopener, noreferrer')}>
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Stack>
                    <ForwardDealsModern />
                </Stack>
            </Stack>
        </Popover>
        {preferencesModalOpen &&  (
            <GlobalPreferencesModal
                isOpen={preferencesModalOpen}
                onClose={() => setPreferencesModalOpen(false)} />
        )}
        {excludeCompanyModalOpen && (
            <ExcludeCompanyModal isOpen={excludeCompanyModalOpen}
                onClose={() => setExcludedCompanyModalOpen(false)} />
        )}
        {manageFieldsModalOpen && (
            <ManageFieldsLoaderModal
                open={manageFieldsModalOpen}
                onClose={() => setManageFieldsModalOpen(false)} />
        )}
    </>);
}

export default withLDConsumer()(GeneralSettings);
